import * as THREE from 'three';

export class XRInput {
    constructor(renderer) {
        this._renderer = renderer;
        this._session = null;
        this._sessionStartListener = null;
        this._inputsourceschangeListener = null;
        this._controllers = [];
        this._buttonHoldState = {};
        this._raycaster = new THREE.Raycaster();
        this._frameSnapshot = null;
    }

    start() {
        const session = this._renderer.xr.getSession();
        if (session) {
            this._setupSession(session);
        }
        this._sessionStartListener = (event) => {
            this._setupSession(event.session);
        };
        this._renderer.xr.addEventListener('sessionstart', this._sessionStartListener);
    }

    _setupSession(session) {
        this._session = session;
        this._controllers = [];
        for (let i = 0; i < 2; i++) {
            const controller = this._renderer.xr.getController(i);
            if (controller) {
                controller.addEventListener('connected', (event) => {
                    controller.userData.inputSource = event.data;
                });
                this._controllers.push(controller);
            }
        }
        this._inputsourceschangeListener = (event) => {
            // Left empty intentionally
        };
        session.addEventListener('inputsourceschange', this._inputsourceschangeListener);
    }

    _getSnapshot() {
        if (!this._frameSnapshot) {
            const session = this._renderer?.xr?.getSession();
            if (!session || !session.inputSources) return;

            const inputSources = session.inputSources;
            this._frameSnapshot = [];

            for (let i = 0; i < inputSources.length; i++) {
                const controller = this._renderer.xr.getController(i);
                // const grip = this._renderer.xr.getControllerGrip(i);
                const gamepad = inputSources[i].gamepad;
                const matrixWorld = controller?.matrixWorld;

                const position = new THREE.Vector3();
                const orientation = new THREE.Quaternion();

                if (matrixWorld) {
                    position.setFromMatrixPosition(matrixWorld);
                    orientation.setFromRotationMatrix(matrixWorld);
                }

                this._frameSnapshot.push({
                    index: i,
                    inputSource: inputSources[i],
                    handedness: inputSources[i].handedness || null,
                    gamepad: gamepad ? {
                        buttons: gamepad.buttons.map(b => ({
                            pressed: b.pressed,
                            touched: b.touched,
                            value: b.value
                        })),
                        axes: [...gamepad.axes]
                    } : null,
                    position,
                    orientation
                });
            }
        }
    }

    count() {
        this._getSnapshot();
        return this._frameSnapshot?.length || 0;
    }

    controller(index) {
        return this._renderer?.xr?.getController(index) || null;
    }

    controllerGrip(index) {
        return this._renderer?.xr?.getControllerGrip(index) || null;
    }

    axes(index) {
        this._getSnapshot();
        const snapshot = this._frameSnapshot?.[index];
        return snapshot?.gamepad?.axes || [];
    }

    isButtonPressed(index, buttonIndex) {
        this._getSnapshot();
        const snapshot = this._frameSnapshot?.[index];
        return snapshot?.gamepad?.buttons?.[buttonIndex]?.pressed || false;
    }

    isButtonReleased(index, buttonIndex) {
        this._getSnapshot();
        const snapshot = this._frameSnapshot?.[index];
        const key = `${index}_${buttonIndex}`;
        const pressed = snapshot?.gamepad?.buttons?.[buttonIndex]?.pressed || false;

        if (this._buttonHoldState[key]?.wasPressed && !pressed) {
            this._buttonHoldState[key].wasPressed = false;
            return true;
        }

        if (!this._buttonHoldState[key]) {
            this._buttonHoldState[key] = { wasPressed: false };
        }

        this._buttonHoldState[key].wasPressed = pressed;
        return false;
    }

    isButtonPressedFor(index, buttonIndex, durationInSeconds) {
        this._getSnapshot();
        const snapshot = this._frameSnapshot?.[index];
        const key = `${index}_${buttonIndex}`;
        const isPressed = snapshot?.gamepad?.buttons?.[buttonIndex]?.pressed || false;

        let state = this._buttonHoldState[key] || { pressedTime: 0, triggered: false };
        if (isPressed) {
            if (state.pressedTime === 0) {
                state.pressedTime = performance.now();
            }
            const elapsed = (performance.now() - state.pressedTime) / 1000;
            if (elapsed >= durationInSeconds && !state.triggered) {
                state.triggered = true;
                this._buttonHoldState[key] = state;
                return true;
            }
        } else {
            state.pressedTime = 0;
            state.triggered = false;
        }

        this._buttonHoldState[key] = state;
        return false;
    }

    position(index = 0) {
        this._getSnapshot();
        return this._frameSnapshot?.[index]?.position || null;
    }

    orientation(index = 0) {
        this._getSnapshot();
        return this._frameSnapshot?.[index]?.orientation || null;
    }

    raycast(index = 0) {
        this._getSnapshot();
        const position = this.position(index);
        const orientation = this.orientation(index);
        if (!position || !orientation) return null;

        const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(orientation);
        this._raycaster.ray.copy(new THREE.Ray(position, direction));

        return { raycaster: this._raycaster, position: position.clone(), direction: direction.clone() };
    }

    handedness(index = 0) {
        this._getSnapshot();
        return this._frameSnapshot?.[index]?.handedness || null;
    }

    haptics(index = 0, value = 1.0, duration = 100) {
        const snapshot = this._frameSnapshot?.[index];
        const gamepad = snapshot?.inputSource?.gamepad;
        if (!gamepad || !gamepad.hapticActuators) return;
        const actuator = gamepad.hapticActuators[0];
        if (actuator) actuator.pulse(value, duration);
    }

    stop() {
        if (this._sessionStartListener) {
            this._renderer.xr.removeEventListener('sessionstart', this._sessionStartListener);
        }
        if (this._inputsourceschangeListener && this._session) {
            this._session.removeEventListener('inputsourceschange', this._inputsourceschangeListener);
        }
        this._controllers = [];
        this._session = null;
        this._buttonHoldState = {};
        this._frameSnapshot = null;
    }

    eventFrameEnd() {
        this._frameSnapshot = null;
    }
}
